import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Bell,
  Send,
  Smartphone,
  CheckSquare,
  Square,
  Search,
  RefreshCw,
} from "lucide-react";
import axios from "axios";
import { toast } from "react-toastify";
import { server } from "../server";
import { appsList } from "../jsonsFiles/appsList";

export default function QuizNotifications() {
  const [tokens, setTokens] = useState([]);
  const [selectedTokens, setSelectedTokens] = useState([]);
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [imageUrl, setImageUrl] = useState("");
  const [appName, setAppName] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [isSending, setIsSending] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  // Theme classes - Hardcoded Dark Mode
  const bgGradient = "bg-gradient-to-br from-gray-900 to-gray-800";
  const cardBg = "bg-gray-800";
  const cardBorder = "border-gray-700";
  const textPrimary = "text-white";
  const textSecondary = "text-gray-300";
  const textMuted = "text-gray-400";
  const inputBg = "bg-gray-700";
  const inputBorder = "border-gray-600";

  const fetchTokens = async () => {
    try {
      setIsLoading(true);
      // Fetching a large number to support "Select All" for now.
      // Ideal solution would require pagination handling or backend support for "all".
      const params = {
        pageNo: 1,
        pageSize: 1000,
      };
      if (appName) {
        params.appName = appName;
      }

      const { data } = await axios.get(`${server}/quizdevicetoken/all`, {
        params,
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });
      setTokens(data?.data || []);
    } catch (error) {
      console.error("Error fetching tokens:", error);
      toast.error("Failed to load device tokens");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchTokens();
  }, [appName]);

  const handleSelectAll = () => {
    if (selectedTokens.length === filteredTokens.length) {
      setSelectedTokens([]);
    } else {
      setSelectedTokens(filteredTokens.map((t) => t.deviceToken));
    }
  };

  const handleSelectToken = (token) => {
    if (selectedTokens.includes(token)) {
      setSelectedTokens((prev) => prev.filter((t) => t !== token));
    } else {
      setSelectedTokens((prev) => [...prev, token]);
    }
  };

  const handleSendNotification = async (e) => {
    e.preventDefault();
    if (selectedTokens.length === 0) {
      toast.warning("Please select at least one device");
      return;
    }

    setIsSending(true);
    let successCount = 0;
    let failCount = 0;

    // Send notifications sequentially or in parallel batches
    // Using Promise.all for better performance, but need to be careful with rate limits if any
    try {
      const promises = selectedTokens.map((token) =>
        axios
          .post(
            `${server}/fcm/send`,
            {
              deviceToken: token,
              title: title,
              body: body,
              imageUrl: imageUrl,
            },
            {
              headers: {
                Authorization: `Bearer ${localStorage.getItem("token")}`,
                "Content-Type": "application/json",
              },
            }
          )
          .then(() => {
            successCount++;
          })
          .catch((err) => {
            console.error("Failed for token:", token, err);
            failCount++;
          })
      );

      await Promise.all(promises);

      if (successCount > 0) {
        toast.success(`Sent to ${successCount} devices successfully`);
      }
      if (failCount > 0) {
        toast.error(`Failed to send to ${failCount} devices`);
      }

      if (successCount > 0) {
        setTitle("");
        setBody("");
        setImageUrl("");
        setSelectedTokens([]);
      }
    } catch (error) {
      toast.error("An error occurred while sending notifications");
    } finally {
      setIsSending(false);
    }
  };

  const filteredTokens = tokens.filter(
    (t) =>
      t.deviceId?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      t.platform?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className={`min-h-screen ${bgGradient} py-8 px-4 relative`}>
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center gap-4 mb-8">
          <div className="p-3 bg-indigo-600 rounded-2xl shadow-lg">
            <Bell className="w-8 h-8 text-white" />
          </div>
          <div>
            <h1 className={`text-4xl font-bold ${textPrimary}`}>
              Cloud Messaging
            </h1>
            <p className={`${textSecondary}`}>
              Push notifications to quiz app users
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column: Form */}
          <div className="lg:col-span-1">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              className={`${cardBg} rounded-2xl p-6 border ${cardBorder} shadow-xl sticky top-6`}
            >
              <h2
                className={`text-xl font-bold ${textPrimary} mb-6 flex items-center gap-2`}
              >
                <Send className="w-5 h-5" /> Compose
              </h2>

              <form onSubmit={handleSendNotification} className="space-y-4">
                <div>
                  <label
                    className={`block text-sm font-medium ${textSecondary} mb-1`}
                  >
                    Select App (Filter)
                  </label>
                  <select
                    value={appName}
                    onChange={(e) => setAppName(e.target.value)}
                    className={`w-full px-4 py-2 rounded-lg border ${inputBg} ${inputBorder} ${textPrimary} focus:ring-2 focus:ring-indigo-500 bg-gray-700 outline-none`}
                  >
                    <option value="">All Apps</option>
                    {appsList?.map((e, i) => (
                      <option key={i} value={e?.name}>
                        {e?.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label
                    className={`block text-sm font-medium ${textSecondary} mb-1`}
                  >
                    Notification Title
                  </label>
                  <input
                    type="text"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    className={`w-full px-4 py-2 rounded-lg border ${inputBg} ${inputBorder} ${textPrimary} focus:ring-2 focus:ring-indigo-500 bg-gray-700 outline-none`}
                    required
                    placeholder="e.g. New Quiz Available!"
                  />
                </div>
                <div>
                  <label
                    className={`block text-sm font-medium ${textSecondary} mb-1`}
                  >
                    Body Message
                  </label>
                  <textarea
                    rows="4"
                    value={body}
                    onChange={(e) => setBody(e.target.value)}
                    className={`w-full px-4 py-2 rounded-lg border ${inputBg} ${inputBorder} ${textPrimary} focus:ring-2 focus:ring-indigo-500 bg-gray-700 outline-none resize-none`}
                    required
                    placeholder="Enter your message here..."
                  />
                </div>
                <div>
                  <label
                    className={`block text-sm font-medium ${textSecondary} mb-1`}
                  >
                    Image URL (Optional)
                  </label>
                  <input
                    type="text"
                    value={imageUrl}
                    onChange={(e) => setImageUrl(e.target.value)}
                    className={`w-full px-4 py-2 rounded-lg border ${inputBg} ${inputBorder} ${textPrimary} focus:ring-2 focus:ring-indigo-500 bg-gray-700 outline-none`}
                    placeholder="https://example.com/image.png"
                  />
                </div>

                <div className="pt-4">
                  <button
                    type="submit"
                    disabled={isSending || selectedTokens.length === 0}
                    className={`w-full py-3 rounded-xl font-bold text-white transition-all shadow-lg flex items-center justify-center gap-2
                      ${
                        isSending || selectedTokens.length === 0
                          ? "bg-gray-600 cursor-not-allowed opacity-50"
                          : "bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 hover:shadow-xl"
                      }`}
                  >
                    {isSending ? (
                      <>
                        <RefreshCw className="w-5 h-5 animate-spin" />{" "}
                        Sending...
                      </>
                    ) : (
                      <>
                        <Send className="w-5 h-5" /> Send to{" "}
                        {selectedTokens.length} Devices
                      </>
                    )}
                  </button>
                </div>
                <div className="text-xs text-center text-gray-500 mt-2">
                  Selected: {selectedTokens.length} / {filteredTokens.length}
                </div>
              </form>
            </motion.div>
          </div>

          {/* Right Column: Device List */}
          <div className="lg:col-span-2">
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              className={`${cardBg} rounded-2xl border ${cardBorder} shadow-xl overflow-hidden flex flex-col h-[80vh]`}
            >
              <div className="p-4 border-b border-gray-700 flex flex-col sm:flex-row gap-4 justify-between items-center bg-gray-800/50">
                <div className="relative w-full sm:w-64">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search devices..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className={`w-full pl-10 pr-4 py-2 rounded-lg bg-gray-900 border border-gray-700 ${textPrimary} focus:outline-none focus:border-indigo-500 text-sm`}
                  />
                </div>
                <div className="flex items-center gap-3 w-full sm:w-auto">
                  <button
                    type="button"
                    onClick={fetchTokens}
                    className="p-2 bg-gray-700 hover:bg-gray-600 rounded-lg text-white transition-colors"
                    title="Refresh List"
                  >
                    <RefreshCw
                      className={`w-4 h-4 ${isLoading ? "animate-spin" : ""}`}
                    />
                  </button>
                  <button
                    type="button"
                    onClick={handleSelectAll}
                    className="flex-1 sm:flex-none px-4 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg text-white text-sm font-medium transition-colors flex items-center gap-2"
                  >
                    {selectedTokens.length === filteredTokens.length &&
                    filteredTokens.length > 0 ? (
                      <CheckSquare className="w-4 h-4 text-indigo-400" />
                    ) : (
                      <Square className="w-4 h-4" />
                    )}
                    Select All
                  </button>
                </div>
              </div>

              <div className="flex-1 overflow-y-auto p-4 space-y-3 custom-scrollbar">
                {isLoading ? (
                  <div className="text-center py-10 text-gray-400">
                    Loading devices...
                  </div>
                ) : filteredTokens.length === 0 ? (
                  <div className="text-center py-10 text-gray-400">
                    No devices found.
                  </div>
                ) : (
                  filteredTokens.map((token) => (
                    <div
                      key={token.id}
                      onClick={() => handleSelectToken(token.deviceToken)}
                      className={`p-3 rounded-xl border cursor-pointer transition-all duration-200 flex items-center gap-4
                                ${
                                  selectedTokens.includes(token.deviceToken)
                                    ? "bg-indigo-900/30 border-indigo-500"
                                    : "bg-gray-900/40 border-gray-700 hover:bg-gray-700/50"
                                }`}
                    >
                      <div
                        className={`p-2 rounded-lg ${
                          selectedTokens.includes(token.deviceToken)
                            ? "bg-indigo-600"
                            : "bg-gray-700"
                        }`}
                      >
                        <Smartphone className="w-5 h-5 text-white" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className={`font-medium truncate ${textPrimary}`}>
                          {token.deviceId || "Unknown Device"}
                        </div>
                        <div className={`text-xs truncate ${textSecondary}`}>
                          Platform: {token.platform || "N/A"} • ID: {token.id}
                        </div>
                        <div
                          className={`text-[10px] truncate ${textMuted} mt-0.5 font-mono`}
                        >
                          {token.deviceToken?.substring(0, 40)}...
                        </div>
                      </div>
                      <div className="flex-shrink-0">
                        {selectedTokens.includes(token.deviceToken) ? (
                          <CheckSquare className="w-6 h-6 text-indigo-400" />
                        ) : (
                          <Square className="w-6 h-6 text-gray-600" />
                        )}
                      </div>
                    </div>
                  ))
                )}
              </div>

              <div className="p-3 border-t border-gray-700 bg-gray-800 text-xs text-center text-gray-500">
                Total Devices: {tokens.length}
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
}
